import { CustomerType } from '@repo/common';
import { Button } from '@repo/shadcn';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';

import { IwForm } from '@components/form';
import { SelectField, TextField } from '@components/form/fields';

import { useModal } from '@/hooks';
import {
    CustomerFormSchema,
    TCustomerForm,
} from '@/modules/customers/customer-form/customer.schema';

type CustomerFormProps = {
    isLoading?: boolean;
    onSubmit: (values: TCustomerForm) => void;
    initialValues: Partial<TCustomerForm>;
    isEditMode?: boolean;
};

export const CustomerForm: FC<CustomerFormProps> = ({
    initialValues,
    isEditMode,
    onSubmit,
    isLoading,
}) => {
    return (
        <IwForm
            schema={CustomerFormSchema}
            logErrors={true}
            initialValues={initialValues}
            onSubmit={(data) => {
                onSubmit(data as TCustomerForm);
            }}
        >
            <div className={'space-y-2'}>
                <TextField<TCustomerForm>
                    name={'name'}
                    label={'Name'}
                    placeholder={'name'}
                />

                <TextField<TCustomerForm>
                    name={'email'}
                    label={'Email'}
                    placeholder={'test@example.com'}
                />
                <TextField<TCustomerForm>
                    name={'phone_number'}
                    label={'Phone Number'}
                    placeholder={'0123456789'}
                />
                <TextField<TCustomerForm>
                    name={'address'}
                    label={'Address'}
                    placeholder={'Dhaka, Bangladesh'}
                />
                <SelectField<TCustomerForm>
                    name={'customer_type'}
                    className={'w-full'}
                    label={'Customer Type'}
                    options={[
                        { label: 'Retail', value: CustomerType.RETAIL },
                        { label: 'Wholesale', value: CustomerType.WHOLESALE },
                    ]}
                />
                <PasswordField />
            </div>
            <FormFooter isLoading={isLoading} isEditMode={isEditMode} />
        </IwForm>
    );
};

CustomerForm.displayName = 'CustomerForm';

const PasswordField = () => {
    const { watch } = useFormContext<TCustomerForm>();
    const customerType = watch('customer_type');

    if (!customerType) {
        return null;
    }

    if (customerType === CustomerType.RETAIL) {
        return null;
    }

    return (
        <div>
            <TextField<TCustomerForm>
                name={'password'}
                label={'Password'}
                type={'password'}
                placeholder={'*****'}
            />
        </div>
    );
};

type FormFooterProps = {
    isEditMode?: boolean;
    isLoading?: boolean;
};
const FormFooter: FC<FormFooterProps> = ({
    isLoading = false,
    isEditMode = false,
}) => {
    const { closeModal } = useModal();

    const { watch } = useFormContext<TCustomerForm>();

    const customerType = watch('customer_type');
    const password = watch('password');

    const renderSubmitText = () => {
        if (isLoading) {
            if (isEditMode) {
                return 'Updating...';
            }
            return 'Creating...';
        }
        if (isEditMode) {
            return 'Update';
        }
        return 'Create';
    };

    const isDisabled =
        isLoading || (customerType === CustomerType.WHOLESALE && !password);

    return (
        <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={closeModal}>
                Cancel
            </Button>
            <Button type="submit" disabled={isDisabled}>
                {renderSubmitText()}
            </Button>
        </div>
    );
};
